import React, { useState, useEffect } from 'react';
import { productAPI } from '../../api';
import { useCart } from '../../context/CartContext';
import LoadingSpinner from '../LoadingSpinner';

// Import all product images
import productImage1 from '../../assets/product1.png';
import productImage2 from '../../assets/product2.png';
import productImage3 from '../../assets/product3.png';
import productImage4 from '../../assets/product4.png';
import productImage5 from '../../assets/product5.png';
import productImage6 from '../../assets/product6.png';
import productImage7 from '../../assets/product7.png';
import productImage8 from '../../assets/product8.png';
import productImage9 from '../../assets/product9.png';
import productImage10 from '../../assets/product10.png';
// Add more imports as needed

const UserProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { addToCart } = useCart();

  // Create a mapping of product titles to images
  const productImages = {
    'Multivitamin Care': productImage1,
    'Liv Super Care': productImage2,
    'Paiels Care': productImage3,
    'Joint Care': productImage4,
    'Power+ Care': productImage5,
    'Lady Care': productImage6,
    'Dant Care': productImage7,
    'Gastric Care': productImage8,
    'Active Power': productImage9,
    'Allergy Care': productImage10,
    // Add more mappings as needed
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAllProducts({ limit: 50 });
      setProducts(response.data?.products || []);
      setError('');
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
  };

  const getProductImage = (product) => {
    // If product has a dynamic image URL, use it
    if (product.image && typeof product.image === 'string' && product.image.trim() !== '') {
      return product.image;
    }
    // Otherwise, use static mapping by name
    const specificImage = productImages[product.name];
    if (specificImage) return specificImage;
    // Fallback to a default image if no match found
    return '/api/placeholder/400/320';
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner fullPage={false} /></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen text-red-600">{error}</div>;

  // Group products by category
  const categories = [
    { key: 'Health_Supplements', label: 'Health Supplements' },
    { key: 'personal_care', label: 'Personal Care' },
    { key: 'fmcg', label: 'FMCG' },
  ];

  const productsByCategory = categories.map(cat => ({
    ...cat,
    items: products.filter(p => p.category === cat.key)
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF6F2] via-[#FDFDFD] to-[#FFF0EA] p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-10">
        <h2 className="text-4xl font-extrabold bg-gradient-to-r from-[#E4572E] to-[#F47C4D] bg-clip-text text-transparent tracking-tight">
          Our Products
        </h2>
        <button
          onClick={fetchProducts}
          className="px-5 py-2.5 rounded-xl bg-white shadow-md border border-gray-200 \
                     hover:shadow-lg hover:scale-105 transition-all duration-300"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Category Sections */}
      {productsByCategory.map(category => (
        <div key={category.key} className="mb-12">
          <h3 className="text-2xl font-bold mb-6 text-[#E4572E]">{category.label}</h3>
          {category.items.length === 0 ? (
            <div className="text-gray-400 italic mb-8">No products in this category.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {category.items.map((product, index) => (
                <div
                  key={index}
                  className="group relative bg-white/70 backdrop-blur-lg border border-gray-200 \
                             rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden"
                >
                  {/* Product Image */}
                  <div className="h-56 overflow-hidden relative">
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/api/placeholder/400/320';
                      }}
                    />
                    {/* Availability Badge */}
                    <div className="absolute top-3 right-3">
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${
                          product.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {product.isActive ? "In Stock" : "Out of Stock"}
                      </span>
                    </div>
                  </div>
                  {/* Product Details */}
                  <div className="p-5">
                    <h3 className="font-bold text-lg text-gray-800 mb-1 truncate">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">
                      {product.category}
                    </p>
                    {/* Price */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-extrabold text-[#E4572E]">
                        ₹ {product.price}
                      </span>
                    </div>
                    {/* Add to Cart Button */}
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={!product.isActive}
                      className={`w-full py-2.5 rounded-xl font-semibold transition-all duration-300 ${
                        product.isActive
                          ? "bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white shadow-md hover:shadow-xl hover:scale-105"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {product.isActive ? "🛒 Add to Cart" : "Out of Stock"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default UserProducts;